import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = axios.create({ baseURL: '/api', timeout: 30000 });
function getToken() {
  try { return JSON.parse(localStorage.getItem('energy_auth') || 'null')?.access_token || ''; }
  catch { return ''; }
}
function getUser() {
  try { return JSON.parse(localStorage.getItem('energy_auth') || 'null')?.user || null; }
  catch { return null; }
}
function authHdr() { return { Authorization: `Bearer ${getToken()}` }; }

const ROLE_META = {
  admin:      { label: 'Admin',      color: '#ef4444', bg: '#ef444420' },
  trader:     { label: 'Trader',     color: '#22c55e', bg: '#22c55e20' },
  compliance: { label: 'Compliance', color: '#3b82f6', bg: '#3b82f620' },
  readonly:   { label: 'Nur-Lesen',  color: '#6b7280', bg: '#6b728020' },
  external:   { label: 'Extern',     color: '#8b5cf6', bg: '#8b5cf620' },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || { label: role, color: '#6b7280', bg: '#6b728020' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.color,
    }}>{m.label}</span>
  );
}

function StatusMsg({ ok, err, onClear }) {
  if (!ok && !err) return null;
  return (
    <div onClick={onClear} style={{
      padding: '10px 14px', borderRadius: 7, marginBottom: 16, cursor: 'pointer',
      background: ok ? '#22c55e15' : '#ef444415',
      border: `1px solid ${ok ? '#22c55e40' : '#ef444440'}`,
      color: ok ? '#22c55e' : '#ef4444', fontSize: 13,
    }}>
      {ok || err}
    </div>
  );
}

// ── Change Password Tab ──────────────────────────────────────────────────────
function MyAccountTab() {
  const [cur, setCur]   = useState('');
  const [next, setNext] = useState('');
  const [conf, setConf] = useState('');
  const [ok, setOk]     = useState('');
  const [err, setErr]   = useState('');
  const [loading, setLoading] = useState(false);

  // Profile state
  const [profile, setProfile]     = useState({ full_name: '', job_title: '', email: '' });
  const [profileOk, setProfileOk] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // TOTP state
  const [totpEnabled, setTotpEnabled]   = useState(false);
  const [totpSetup, setTotpSetup]       = useState(null);  // { secret, qr_png }
  const [totpCode, setTotpCode]         = useState('');
  const [totpLoading, setTotpLoading]   = useState(false);
  const [totpOk, setTotpOk]             = useState('');
  const [totpErr, setTotpErr]           = useState('');
  const [disablePw, setDisablePw]       = useState('');

  // Load profile + TOTP status
  useEffect(() => {
    API.get('/auth/me', { headers: authHdr() })
      .then(r => {
        setTotpEnabled(r.data.totp_enabled);
        setProfile({
          full_name: r.data.full_name || '',
          job_title: r.data.job_title || '',
          email:     r.data.email || '',
        });
      })
      .catch(() => {});
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true); setProfileOk(''); setProfileErr('');
    try {
      await API.put('/auth/me', profile, { headers: authHdr() });
      setProfileOk('Profil gespeichert.');
    } catch (e) {
      setProfileErr(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setProfileLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== conf) { setErr('Passwörter stimmen nicht überein'); return; }
    if (next.length < 8) { setErr('Mindestens 8 Zeichen'); return; }
    setLoading(true); setOk(''); setErr('');
    try {
      await API.post('/auth/change-password',
        { current_password: cur, new_password: next },
        { headers: authHdr() }
      );
      setOk('Passwort erfolgreich geändert.');
      setCur(''); setNext(''); setConf('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally { setLoading(false); }
  };

  const startTotpSetup = async () => {
    setTotpLoading(true); setTotpErr(''); setTotpOk('');
    try {
      const r = await API.post('/auth/totp/setup', {}, { headers: authHdr() });
      setTotpSetup(r.data);
    } catch (e) { setTotpErr(e.response?.data?.detail || 'Fehler'); }
    finally { setTotpLoading(false); }
  };

  const verifyTotpSetup = async () => {
    if (totpCode.length !== 6) { setTotpErr('6-stelligen Code eingeben'); return; }
    setTotpLoading(true); setTotpErr('');
    try {
      await API.post(`/auth/totp/verify-setup?code=${totpCode}`, {}, { headers: authHdr() });
      setTotpEnabled(true); setTotpSetup(null); setTotpCode('');
      setTotpOk('✓ 2FA erfolgreich aktiviert');
    } catch (e) { setTotpErr(e.response?.data?.detail || 'Ungültiger Code'); }
    finally { setTotpLoading(false); }
  };

  const disableTotp = async () => {
    if (!disablePw) { setTotpErr('Passwort eingeben'); return; }
    setTotpLoading(true); setTotpErr('');
    try {
      await API.post('/auth/totp/disable',
        { current_password: disablePw, new_password: disablePw },  // new_password field required by model
        { headers: authHdr() }
      );
      setTotpEnabled(false); setDisablePw('');
      setTotpOk('2FA wurde deaktiviert');
    } catch (e) { setTotpErr(e.response?.data?.detail || 'Fehler'); }
    finally { setTotpLoading(false); }
  };

  const inputSt = { width: '100%', padding: '8px 12px', borderRadius: 7,
    background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 };
  const labelSt = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 };

  return (
    <div style={{ maxWidth: 480 }}>
      {/* Profile */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text1)', marginBottom: 16 }}>Mein Profil</div>
        {profileOk && <div style={{ padding: '8px 12px', background: '#22c55e15', border: '1px solid #22c55e40',
          borderRadius: 7, color: '#22c55e', fontSize: 13, marginBottom: 12 }}>{profileOk}</div>}
        {profileErr && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
          borderRadius: 7, color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{profileErr}</div>}
        <form onSubmit={saveProfile}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelSt}>Name</label>
            <input type="text" value={profile.full_name}
              onChange={e => setProfile({ ...profile, full_name: e.target.value })}
              placeholder="Vor- und Nachname" style={inputSt} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelSt}>Funktion / Position</label>
            <input type="text" value={profile.job_title}
              onChange={e => setProfile({ ...profile, job_title: e.target.value })}
              placeholder="z.B. Senior Trader" style={inputSt} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelSt}>E-Mail</label>
            <input type="email" value={profile.email}
              onChange={e => setProfile({ ...profile, email: e.target.value })}
              style={inputSt} />
          </div>
          <button type="submit" disabled={profileLoading}
            style={{ padding: '9px 20px', background: '#22c55e', color: '#000',
              border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13,
              cursor: profileLoading ? 'not-allowed' : 'pointer', opacity: profileLoading ? .7 : 1 }}>
            {profileLoading ? 'Speichere...' : 'Profil speichern'}
          </button>
        </form>
      </div>

      {/* Password Change */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24, marginBottom: 32 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text1)', marginBottom: 16 }}>Passwort ändern</div>
        <StatusMsg ok={ok} err={err} onClear={() => { setOk(''); setErr(''); }} />
        <form onSubmit={handleSubmit}>
          {[
            { label: 'Aktuelles Passwort', val: cur, set: setCur },
            { label: 'Neues Passwort (min. 12 Zeichen)', val: next, set: setNext },
            { label: 'Neues Passwort bestätigen', val: conf, set: setConf },
          ].map(({ label, val, set }) => (
            <div key={label} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 5 }}>
                {label}
              </label>
              <input type="password" value={val} onChange={e => set(e.target.value)} required
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  color: 'var(--text1)', fontSize: 13 }} />
            </div>
          ))}
          <button type="submit" disabled={loading}
            style={{ padding: '9px 20px', background: '#22c55e', color: '#000',
              border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? .7 : 1 }}>
            {loading ? 'Speichere...' : 'Passwort ändern'}
          </button>
        </form>
      </div>

      {/* TOTP / 2FA */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text1)' }}>🔐 Zwei-Faktor-Authentifizierung</div>
          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
            background: totpEnabled ? '#22c55e20' : '#6b728020',
            color: totpEnabled ? '#22c55e' : '#6b7280' }}>
            {totpEnabled ? 'Aktiv' : 'Inaktiv'}
          </span>
        </div>

        {totpOk && (
          <div style={{ padding: '8px 12px', background: '#22c55e15', border: '1px solid #22c55e40',
            borderRadius: 7, color: '#22c55e', fontSize: 13, marginBottom: 12 }}>{totpOk}</div>
        )}
        {totpErr && (
          <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
            borderRadius: 7, color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{totpErr}</div>
        )}

        {!totpEnabled && !totpSetup && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>
              Schütze deinen Account mit einem Authenticator (Google Authenticator, Authy, etc.).
              Nach der Einrichtung wird bei jedem Login ein zeitbasierter Code abgefragt.
            </p>
            <button onClick={startTotpSetup} disabled={totpLoading}
              style={{ padding: '8px 18px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {totpLoading ? 'Generiere…' : '+ 2FA einrichten'}
            </button>
          </div>
        )}

        {!totpEnabled && totpSetup && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
              Scanne den QR-Code mit deiner Authenticator-App, dann bestätige mit dem generierten Code.
            </p>
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <img src={`data:image/png;base64,${totpSetup.qr_png}`} alt="QR Code"
                style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Manueller Schlüssel:</div>
                <code style={{ fontSize: 12, background: 'var(--bg-deep)', padding: '6px 10px', borderRadius: 6,
                  color: '#22c55e', display: 'block', wordBreak: 'break-all', marginBottom: 14 }}>
                  {totpSetup.secret}
                </code>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>6-stelliger Bestätigungscode:</div>
                <input
                  type="text" value={totpCode} maxLength={6} placeholder="000000"
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 7,
                    background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)',
                    fontSize: 20, textAlign: 'center', letterSpacing: '.3em', fontWeight: 700,
                    marginBottom: 12 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={verifyTotpSetup} disabled={totpLoading}
                    style={{ padding: '8px 16px', background: '#22c55e', color: '#000',
                      border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    {totpLoading ? 'Prüfe…' : '✓ Aktivieren'}
                  </button>
                  <button onClick={() => { setTotpSetup(null); setTotpCode(''); setTotpErr(''); }}
                    style={{ padding: '8px 14px', background: 'transparent', color: 'var(--text2)',
                      border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {totpEnabled && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>
              2FA ist aktiv. Zur Deaktivierung dein aktuelles Passwort eingeben.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="password" value={disablePw} onChange={e => setDisablePw(e.target.value)}
                placeholder="Aktuelles Passwort"
                style={{ flex: 1, padding: '8px 12px', borderRadius: 7,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }} />
              <button onClick={disableTotp} disabled={totpLoading}
                style={{ padding: '8px 14px', background: '#ef444420', color: '#ef4444',
                  border: '1px solid #ef444440', borderRadius: 7, fontWeight: 700, fontSize: 13,
                  cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {totpLoading ? 'Deaktiviere…' : '2FA deaktivieren'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create User Modal ────────────────────────────────────────────────────────
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'readonly', full_name: '', job_title: '' });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setErr('Passwort mind. 8 Zeichen'); return; }
    setLoading(true); setErr('');
    try {
      await API.post('/auth/users', form, { headers: authHdr() });
      onCreated();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000080',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 420, maxWidth: '95vw' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Neuer Benutzer</div>
        {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
          borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{err}</div>}
        <form onSubmit={handle}>
          {[
            { label: 'Benutzername *', key: 'username', type: 'text', required: true },
            { label: 'Name', key: 'full_name', type: 'text', required: false },
            { label: 'Funktion / Position', key: 'job_title', type: 'text', required: false },
            { label: 'E-Mail', key: 'email', type: 'email', required: false },
            { label: 'Passwort (min. 8 Zeichen) *', key: 'password', type: 'password', required: true },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
                {f.label}
              </label>
              <input type={f.type} value={form[f.key]} required={f.required}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  color: 'var(--text1)', fontSize: 13 }} />
            </div>
          ))}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
              Rolle
            </label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 7,
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                color: 'var(--text1)', fontSize: 13 }}>
              <option value="external">Extern (Portal)</option>
              <option value="readonly">Nur-Lesen</option>
              <option value="compliance">Compliance</option>
              <option value="trader">Trader</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text2)',
                border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '8px 16px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? .7 : 1 }}>
              {loading ? '...' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Set Password Modal ───────────────────────────────────────────────────────
function SetPasswordModal({ user, onClose, onSaved }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr('Mindestens 8 Zeichen'); return; }
    setLoading(true); setErr('');
    try {
      await API.put(`/auth/users/${user.id}/password`, { password: pw }, { headers: authHdr() });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000080',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 360, maxWidth: '95vw' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Passwort setzen</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
          Benutzer: <strong style={{ color: 'var(--text1)' }}>{user.username}</strong>
        </div>
        {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
          borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <form onSubmit={handle}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
              Neues Passwort (min. 8 Zeichen)
            </label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} required autoFocus
              style={{ width: '100%', padding: '8px 12px', borderRadius: 7,
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                color: 'var(--text1)', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text2)',
                border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '8px 16px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13,
                cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '...' : 'Setzen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit User Modal ───────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    email:     user.email     || '',
    role:      user.role,
    full_name: user.full_name || '',
    job_title: user.job_title || '',
  });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await API.put(`/auth/users/${user.id}`, form, { headers: authHdr() });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000080',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 360, maxWidth: '95vw' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Benutzer bearbeiten</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
          <strong style={{ color: 'var(--text1)' }}>{user.username}</strong>
        </div>
        {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
          borderRadius: 6, color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <form onSubmit={handle}>
          {[
            { label: 'Name', key: 'full_name', type: 'text', placeholder: 'Vor- und Nachname' },
            { label: 'Funktion / Position', key: 'job_title', type: 'text', placeholder: 'z.B. Senior Trader' },
            { label: 'E-Mail', key: 'email', type: 'email', placeholder: '' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
                {f.label}
              </label>
              <input type={f.type} value={form[f.key]} placeholder={f.placeholder}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }} />
            </div>
          ))}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>
              Rolle
            </label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 7,
                background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text1)', fontSize: 13 }}>
              <option value="external">Extern (Portal)</option>
              <option value="readonly">Nur-Lesen</option>
              <option value="compliance">Compliance</option>
              <option value="trader">Trader</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text2)',
                border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
              Abbrechen
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '8px 16px', background: '#22c55e', color: '#000',
                border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {loading ? '...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Email Settings Tab ────────────────────────────────────────────────────────
function EmailSettingsTab() {
  const DEFAULTS = {
    smtp_enabled: 'false', smtp_host: '', smtp_port: '587',
    smtp_user: '', smtp_password: '', smtp_from: '',
    smtp_from_name: 'Energy BPOS', smtp_tls: 'true',
    app_url: 'https://bpos.simplimizer.com',
  };
  const [form, setForm]       = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testTo, setTestTo]   = useState('');
  const [sending, setSending] = useState(false);
  const [ok, setOk]           = useState('');
  const [err, setErr]         = useState('');

  const load = () => {
    setLoading(true);
    API.get('/settings/email', { headers: authHdr() })
      .then(r => setForm({ ...DEFAULTS, ...r.data }))
      .catch(() => setErr('Einstellungen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setOk(''); setErr('');
    try {
      await API.put('/settings/email', form, { headers: authHdr() });
      setOk('✓ Einstellungen gespeichert.');
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setSending(true); setOk(''); setErr('');
    try {
      const r = await API.post('/settings/email/test', { to: testTo }, { headers: authHdr() });
      setOk(`✓ Test-E-Mail gesendet an ${r.data.sent_to}`);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Senden');
    } finally { setSending(false); }
  };

  if (loading) return <div style={{ color: 'var(--text3)', padding: 20 }}>Lade…</div>;

  const inSt = { width: '100%', padding: '8px 12px', borderRadius: 7,
    background: 'var(--bg-surface)', border: '1px solid var(--border)',
    color: 'var(--text1)', fontSize: 13 };
  const labSt = { display: 'block', fontSize: 12, fontWeight: 600,
    color: 'var(--text2)', marginBottom: 5 };
  const isEnabled = form.smtp_enabled === 'true';

  return (
    <div style={{ maxWidth: 580 }}>
      {/* Status banner */}
      <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 20,
        background: isEnabled ? '#22c55e15' : '#f59e0b15',
        border: `1px solid ${isEnabled ? '#22c55e40' : '#f59e0b40'}`,
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>{isEnabled ? '✅' : '⚠️'}</span>
        <span style={{ fontSize: 13, fontWeight: 600,
          color: isEnabled ? '#22c55e' : '#f59e0b' }}>
          {isEnabled ? `SMTP aktiv — ${form.smtp_host}:${form.smtp_port}` : 'SMTP deaktiviert'}
        </span>
      </div>

      {ok && <div style={{ padding: '8px 12px', background: '#22c55e15', border: '1px solid #22c55e40',
        borderRadius: 7, color: '#22c55e', fontSize: 13, marginBottom: 14 }}>{ok}</div>}
      {err && <div style={{ padding: '8px 12px', background: '#ef444415', border: '1px solid #ef444440',
        borderRadius: 7, color: '#ef4444', fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <form onSubmit={save}>
        {/* Enable toggle */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">Aktivierung</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <div onClick={() => setForm(f => ({ ...f, smtp_enabled: f.smtp_enabled === 'true' ? 'false' : 'true' }))}
              style={{ width: 44, height: 24, borderRadius: 12, transition: 'background .2s',
                background: isEnabled ? '#22c55e' : 'var(--border)',
                position: 'relative', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 3, left: isEnabled ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left .2s', boxShadow: '0 1px 3px #0004' }} />
            </div>
            <span style={{ fontSize: 13, color: 'var(--text1)', fontWeight: 600 }}>
              {isEnabled ? 'E-Mail Versand aktiviert' : 'E-Mail Versand deaktiviert'}
            </span>
          </label>
        </div>

        {/* SMTP Config */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">SMTP Server</div>
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div>
              <label style={labSt}>SMTP Host</label>
              <input type="text" value={form.smtp_host} placeholder="smtp.office365.com"
                onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))}
                style={inSt} />
            </div>
            <div>
              <label style={labSt}>Port</label>
              <input type="number" value={form.smtp_port} placeholder="587"
                onChange={e => setForm(f => ({ ...f, smtp_port: e.target.value }))}
                style={inSt} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labSt}>Benutzername / E-Mail</label>
            <input type="text" value={form.smtp_user} placeholder="user@example.com"
              onChange={e => setForm(f => ({ ...f, smtp_user: e.target.value }))}
              style={inSt} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labSt}>Passwort</label>
            <input type="password" value={form.smtp_password}
              placeholder="Leer lassen um bestehendes Passwort zu behalten"
              onChange={e => setForm(f => ({ ...f, smtp_password: e.target.value }))}
              style={inSt} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 4 }}>
            <input type="checkbox" checked={form.smtp_tls === 'true'}
              onChange={e => setForm(f => ({ ...f, smtp_tls: e.target.checked ? 'true' : 'false' }))}
              style={{ accentColor: '#22c55e', width: 15, height: 15 }} />
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>STARTTLS / TLS verwenden</span>
          </label>
        </div>

        {/* Sender & App */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">Absender & App</div>
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div>
              <label style={labSt}>Absender-Adresse</label>
              <input type="email" value={form.smtp_from} placeholder="noreply@example.com"
                onChange={e => setForm(f => ({ ...f, smtp_from: e.target.value }))}
                style={inSt} />
            </div>
            <div>
              <label style={labSt}>Absender-Name</label>
              <input type="text" value={form.smtp_from_name} placeholder="Energy BPOS"
                onChange={e => setForm(f => ({ ...f, smtp_from_name: e.target.value }))}
                style={inSt} />
            </div>
          </div>
          <div>
            <label style={labSt}>App-URL (für Login-Links in E-Mails)</label>
            <input type="text" value={form.app_url} placeholder="https://bpos.simplimizer.com"
              onChange={e => setForm(f => ({ ...f, app_url: e.target.value }))}
              style={inSt} />
          </div>
        </div>

        <button type="submit" disabled={saving}
          style={{ padding: '10px 24px', background: '#22c55e', color: '#000',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .7 : 1,
            marginBottom: 24 }}>
          {saving ? 'Speichere…' : '💾 Einstellungen speichern'}
        </button>
      </form>

      {/* Triggers */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">E-Mail Trigger</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { icon: '👤', label: 'Neuer Benutzer', desc: 'Welcome-E-Mail mit Zugangsdaten' },
            { icon: '🔑', label: 'Passwort-Reset', desc: 'Neue Zugangsdaten bei Admin-Passwort-Reset' },
          ].map(t => (
            <div key={t.label} style={{ display: 'flex', gap: 12, alignItems: 'center',
              padding: '8px 12px', background: 'var(--bg-surface)',
              borderRadius: 7, border: '1px solid var(--border)' }}>
              <span>{t.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{t.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t.desc}</div>
              </div>
              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, fontWeight: 600,
                background: isEnabled ? '#22c55e20' : '#6b728020',
                color: isEnabled ? '#22c55e' : 'var(--text3)' }}>
                {isEnabled ? 'Aktiv' : 'Inaktiv'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Test */}
      <div className="card">
        <div className="card-title">Test-E-Mail senden</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
            placeholder="empfaenger@example.com (leer = eigene Adresse)"
            style={{ ...inSt, flex: 1 }} />
          <button onClick={sendTest} disabled={sending || !isEnabled}
            title={!isEnabled ? 'SMTP nicht aktiviert' : ''}
            style={{ padding: '8px 16px', background: '#22c55e', color: '#000',
              border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13,
              cursor: (sending || !isEnabled) ? 'not-allowed' : 'pointer',
              opacity: (sending || !isEnabled) ? .5 : 1, whiteSpace: 'nowrap' }}>
            {sending ? 'Sende…' : '📧 Test senden'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
function UsersTab() {
  const currentUser = getUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState('');
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [pwModal, setPwModal] = useState(null);   // user object
  const [editModal, setEditModal] = useState(null); // user object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/auth/users', { headers: authHdr() });
      setUsers(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    if (u.id === currentUser?.id) return;
    try {
      await API.put(`/auth/users/${u.id}`, { is_active: !u.is_active }, { headers: authHdr() });
      setOk(`${u.username} ${!u.is_active ? 'aktiviert' : 'deaktiviert'}.`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div>
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { load(); setOk('Benutzer erstellt.'); }} />}
      {pwModal && <SetPasswordModal user={pwModal} onClose={() => setPwModal(null)} onSaved={() => setOk('Passwort gesetzt.')} />}
      {editModal && <EditUserModal user={editModal} onClose={() => setEditModal(null)} onSaved={() => { load(); setOk('Gespeichert.'); }} />}

      <StatusMsg ok={ok} err={err} onClear={() => { setOk(''); setErr(''); }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>
          {loading ? 'Lade...' : `${users.length} Benutzer`}
        </div>
        <button onClick={() => setShowCreate(true)}
          style={{ padding: '7px 14px', background: '#22c55e', color: '#000',
            border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Neuer Benutzer
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Benutzer', 'Rolle', 'Status', 'Letzter Login', 'Aktionen'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11,
                  fontWeight: 700, color: 'var(--text3)', borderBottom: '1px solid var(--border)',
                  textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isMe = u.id === currentUser?.id;
              return (
                <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--bg-hover)' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {u.username}
                      {isMe && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px',
                        background: '#22c55e20', color: '#22c55e', borderRadius: 4 }}>ICH</span>}
                    </div>
                    {u.full_name && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 1 }}>{u.full_name}</div>}
                    {u.job_title && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{u.job_title}</div>}
                    {u.email && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{u.email}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--bg-hover)' }}>
                    <RoleBadge role={u.role} />
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--bg-hover)' }}>
                    <span style={{ fontSize: 12, color: u.is_active ? '#22c55e' : '#ef4444' }}>
                      {u.is_active ? '● Aktiv' : '○ Inaktiv'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--bg-hover)',
                    fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    {formatDate(u.last_login)}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--bg-hover)' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button onClick={() => setPwModal(u)}
                        style={{ padding: '4px 10px', background: '#3b82f620', color: '#3b82f6',
                          border: '1px solid #3b82f640', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                        🔑 Passwort
                      </button>
                      <button onClick={() => setEditModal(u)}
                        style={{ padding: '4px 10px', background: '#22c55e15', color: '#22c55e',
                          border: '1px solid #22c55e30', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                        ✏️ Bearbeiten
                      </button>
                      {!isMe && (
                        <button onClick={() => toggleActive(u)}
                          style={{ padding: '4px 10px',
                            background: u.is_active ? '#ef444415' : '#22c55e15',
                            color: u.is_active ? '#ef4444' : '#22c55e',
                            border: `1px solid ${u.is_active ? '#ef444430' : '#22c55e30'}`,
                            borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          {u.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserManagementPage() {
  const currentUser = getUser();
  const isAdmin = currentUser?.role === 'admin';
  const [tab, setTab] = useState('account');

  const TABS = [
    { id: 'account', label: 'Mein Konto' },
    ...(isAdmin ? [{ id: 'users', label: 'Benutzer' }] : []),
    ...(isAdmin ? [{ id: 'email', label: '📧 E-Mail' }] : []),
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)' }}>Benutzerverwaltung</h1>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
          Kontoverwaltung & Benutzerpflege
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 18px', background: 'none', border: 'none',
            borderBottom: tab === t.id ? '2px solid #22c55e' : '2px solid transparent',
            color: tab === t.id ? '#22c55e' : 'var(--text2)',
            fontWeight: tab === t.id ? 600 : 400,
            cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'account' && <MyAccountTab />}
      {tab === 'users'   && isAdmin && <UsersTab />}
      {tab === 'email'   && isAdmin && <EmailSettingsTab />}
    </div>
  );
}
